import { InputBase, Box, alpha, useMediaQuery, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useState, useRef, useCallback, useEffect, KeyboardEvent } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "@/contexts/AuthContext";
import { useCombinedSearch } from "@/hooks/useSearch";
import { SearchDropdown } from "./SearchDropdown";

/**
 * Main search bar component with dropdown results.
 * Supports keyboard navigation and instant navigation search.
 */
export function SearchBar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { user } = useAuth();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // State
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Get user role (default to 'user' if not available)
  const userRole = user?.role ?? "user";

  // Search results
  const { navigationResults, apiResults, total, isLoading } = useCombinedSearch(
    query,
    userRole as "user" | "manager" | "director" | "administrator",
    isOpen
  );

  // Total results count for keyboard navigation
  const totalResults = navigationResults.length + apiResults.length;

  // Placeholder text
  const placeholder = isMobile ? "Search..." : "Search Pathways (Cmd+K)";

  // Open dropdown when query has 2+ chars
  useEffect(() => {
    if (query.length >= 2) {
      setIsOpen(true);
      setSelectedIndex(0);
    } else {
      setIsOpen(false);
    }
  }, [query]);

  // Global keyboard shortcut (Cmd+K / Ctrl+K)
  useEffect(() => {
    const handleGlobalKeydown = (e: globalThis.KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };

    document.addEventListener("keydown", handleGlobalKeydown);
    return () => document.removeEventListener("keydown", handleGlobalKeydown);
  }, []);

  // Handle close
  const handleClose = useCallback(() => {
    setIsOpen(false);
    setQuery("");
    setSelectedIndex(0);
  }, []);

  // Handle navigation to a result
  const handleNavigate = useCallback(() => {
    handleClose();
    inputRef.current?.blur();
  }, [handleClose]);

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || totalResults === 0) {
      if (e.key === "Escape") {
        handleClose();
        inputRef.current?.blur();
      }
      return;
    }

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => (prev + 1) % totalResults);
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev - 1 + totalResults) % totalResults);
        break;
      case "Enter":
        e.preventDefault();
        // Navigate to selected result
        if (selectedIndex >= 0 && selectedIndex < totalResults) {
          // Find the result at this index
          if (selectedIndex < navigationResults.length) {
            const navResult = navigationResults[selectedIndex];
            navigate(navResult.path);
          } else {
            const apiResult = apiResults[selectedIndex - navigationResults.length];
            navigate(apiResult.url);
          }
          handleNavigate();
        }
        break;
      case "Escape":
        e.preventDefault();
        handleClose();
        inputRef.current?.blur();
        break;
    }
  };

  return (
    <Box ref={containerRef} sx={{ position: "relative", width: "100%" }}>
      <Box
        sx={(theme) => ({
          position: "relative",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.common.black, 0.04),
          "&:hover": {
            backgroundColor: alpha(theme.palette.common.black, 0.06),
          },
          width: "100%",
        })}
      >
        <Box
          sx={{
            padding: { xs: "0 12px", md: "0 16px" },
            height: "100%",
            position: "absolute",
            pointerEvents: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "text.secondary",
          }}
        >
          <SearchIcon fontSize={isMobile ? "small" : "medium"} />
        </Box>
        <InputBase
          inputRef={inputRef}
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query.length >= 2) {
              setIsOpen(true);
            }
          }}
          inputProps={{
            "aria-label": "Search Pathways",
            "aria-autocomplete": "list",
            "aria-controls": isOpen ? "search-results" : undefined,
            "aria-expanded": isOpen,
            role: "combobox",
          }}
          sx={{
            color: "inherit",
            width: "100%",
            "& .MuiInputBase-input": {
              padding: { xs: "8px 8px 8px 40px", md: "10px 10px 10px 48px" },
              width: "100%",
              fontSize: { xs: "0.875rem", md: "1rem" },
            },
          }}
        />
      </Box>

      {/* Search dropdown */}
      <SearchDropdown
        open={isOpen}
        anchorEl={containerRef.current}
        navigationResults={navigationResults}
        apiResults={apiResults}
        total={total}
        isLoading={isLoading}
        selectedIndex={selectedIndex}
        onSelect={setSelectedIndex}
        onClose={handleClose}
        onNavigate={handleNavigate}
        query={query}
      />
    </Box>
  );
}
